import { cn } from "@shared/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  AlertTriangle,
  ArrowDown,
  Calendar,
  Clock,
  Printer,
} from "lucide-react";
import React from "react";

interface MetricsCardProps {
  title: string;
  value: number | string;
  description?: string;
  icon?: React.ReactNode | string;
  onClick?: () => void;
  variant?: "default" | "success" | "warning" | "destructive" | "info";
  className?: string;
}

export function MetricsCard({
  title,
  value,
  description,
  icon,
  onClick,
  variant = "default",
  className,
}: MetricsCardProps) {
  const variantStyles = {
    default: "",
    success: "border-l-4 border-l-success",
    warning: "border-l-4 border-l-warning",
    destructive: "border-l-4 border-l-destructive",
    info: "border-l-4 border-l-primary",
  };

  // Map icon names to components
  const iconComponents: Record<string, React.ReactNode> = {
    calendar: <Calendar className="h-4 w-4" />,
    clock: <Clock className="h-4 w-4" />,
    printer: <Printer className="h-4 w-4" />,
    alert: <AlertTriangle className="h-4 w-4" />,
    "arrow-down": <ArrowDown className="h-4 w-4" />,
  };

  // Background colors for icons based on variant
  const iconBackgroundColors = {
    default: "bg-gray-100 text-gray-500",
    success: "bg-green-100 text-green-600",
    warning: "bg-amber-100 text-amber-600",
    destructive: "bg-red-100 text-red-600",
    info: "bg-blue-100 text-blue-600",
  };

  // Get the icon component if a string was provided
  const IconComponent = typeof icon === "string" ? iconComponents[icon] : icon;

  return (
    <Card
      className={cn(
        "h-full transition-all hover:shadow-md",
        onClick && "cursor-pointer",
        variantStyles[variant],
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {IconComponent && (
          <div
            className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center",
              iconBackgroundColors[variant]
            )}
          >
            {IconComponent}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <CardDescription className="mt-1 text-xs">
            {description}
          </CardDescription>
        )}
      </CardContent>
    </Card>
  );
}
